/**
 * PinchViewerFrame — 고정 높이 핀치/팬 viewer 래핑 공통 컴포넌트.
 *
 * 소비처는 `ComposedPinchViewport`(COMPOSED 낱장 묶음) 하나다 — viewer 래핑(고정 높이 viewport +
 * react-zoom-pan-pinch)의 단일 출처다.
 *
 * 동작:
 * - 터치 기기 + 비controlled일 때만 활성(`enabled`). 데스크탑/controlled는 호출처가 끄고 네이티브 스크롤.
 * - viewer 자신의 화면상 top(getBoundingClientRect)을 측정해 `100dvh - top - 하단여백`을 높이로 고정.
 *   콘텐츠(2페이지+)가 창보다 크므로 1-finger 드래그 = 세로 pan(페이지 이동), 핀치 = 줌.
 * - resize·페이지 수 변동(remeasureKey)·viewer 위치 변화(ResizeObserver)에 재측정.
 * - 단일 CSS transform(translate+scale, focal 공식)이라 떨림/좌상단 점프 근절. doubleClick reset으로 fit 복귀.
 *
 * `enabled=false`면 children을 `overflow-x-auto`로 그대로 깔아 기존 정적 렌더(네이티브 스크롤) 유지.
 */
import { useRef, useState, useEffect, useCallback, type ReactNode } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface PinchViewerFrameProps {
  /** viewer 안에 들어갈 A4 페이지 묶음(정적 양식). */
  children: ReactNode;
  /**
   * 고정 높이 핀치 viewer 활성 여부. 호출처가 (터치 + 비controlled + opt-in) 조건을 계산해 전달한다.
   * false면 children을 overflow-x-auto로 그대로 렌더(네이티브 스크롤, 회귀 0).
   */
  enabled: boolean;
  /**
   * viewer 높이 재측정 트리거 키 — 페이지 수 등 레이아웃을 바꾸는 값이 변하면 갱신.
   * (값이 바뀔 때마다 measureViewerHeight를 재실행한다.)
   */
  remeasureKey?: number;
}

export function PinchViewerFrame({ children, enabled, remeasureKey }: PinchViewerFrameProps) {
  // 고정 높이 뷰어(P5): viewer를 화면 가시영역 높이로 고정해 그 창보다 큰 콘텐츠(2페이지+)를
  // 1-finger 드래그로 세로 pan(페이지 이동)할 수 있게 한다.
  // - 호스트는 window(document) 스크롤 + 일반 flow라 flex-1로 남은 공간을 못 잡으므로,
  //   viewer 자신의 화면상 top(getBoundingClientRect)을 측정해 `100dvh - top - 하단여백`을 높이로 쓴다.
  //   (헤더 sticky·하단 액션바 fixed와 겹치지 않게 — top은 헤더 아래에서 시작, 하단여백은 액션바 높이 + 여유.)
  // - resize·주소창 변화·페이지 수 변동에 ResizeObserver + resize 리스너로 재측정.
  const viewerRef = useRef<HTMLDivElement>(null);
  const [viewerHeight, setViewerHeight] = useState<number | null>(null);

  const measureViewerHeight = useCallback(() => {
    const node = viewerRef.current;
    if (!node) return;
    const top = node.getBoundingClientRect().top;
    // 하단 고정 액션바(약 64px) + 여유. 안전상 넉넉히 96px 확보.
    const BOTTOM_RESERVE = 96;
    const available = window.innerHeight - top - BOTTOM_RESERVE;
    // 너무 작아지지 않도록 하한(최소 화면의 절반).
    setViewerHeight(Math.max(available, Math.floor(window.innerHeight * 0.5)));
  }, []);

  // 뷰포트 리사이즈 시 고정 높이 재측정(활성 분기에서만).
  useEffect(() => {
    if (!enabled) return;
    const onResize = () => measureViewerHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, measureViewerHeight]);

  // 마운트/페이지 수 변동(remeasureKey)/레이아웃 변화 시 고정 높이 측정.
  // ResizeObserver로 viewer 자신의 화면상 위치(상단 chrome 높이 변화 등)도 추종.
  useEffect(() => {
    if (!enabled) return;
    measureViewerHeight();
    const node = viewerRef.current;
    if (!node) return;
    const ro = new ResizeObserver(() => measureViewerHeight());
    ro.observe(node);
    return () => ro.disconnect();
  }, [enabled, measureViewerHeight, remeasureKey]);

  if (!enabled) {
    // 데스크탑/controlled: 기존 정적 렌더 + 네이티브 가로/세로 스크롤(회귀 0).
    return <div className="overflow-x-auto">{children}</div>;
  }

  // 터치 기기: 고정 높이 viewport 안에서 react-zoom-pan-pinch로 1-finger 드래그 pan + 핀치 줌.
  // - viewer를 화면 가시영역 높이로 고정(viewerHeight) → 콘텐츠(2페이지+)가 창보다 크므로
  //   1-finger 드래그가 세로 pan(페이지 이동)으로 동작한다(라이브러리 표준 패턴).
  //   고정 높이가 없으면 wrapper가 콘텐츠 전체 높이를 차지해 pan 여백 0 → 드래그 먹통(P4 버그).
  // - 단일 CSS transform(translate+scale, focal 공식)이라 자체 scroll 보정의 떨림/좌상단 점프 근절.
  // - scale 1 = fit 상태(추가 줌 없음), maxScale 3까지 확대. doubleClick reset으로 fit 복귀.
  // - limitToBounds로 경계 밖 못 나감. initialPositionY=0(최상단부터 보기).
  // - wheel 비활성(데스크탑은 어차피 미적용이나 안전상 명시).
  return (
    <div
      ref={viewerRef}
      style={{ height: viewerHeight ?? '70dvh', overflow: 'hidden', touchAction: 'none' }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={3}
        initialPositionX={0}
        initialPositionY={0}
        limitToBounds
        doubleClick={{ mode: 'reset' }}
        wheel={{ disabled: true }}
        panning={{ velocityDisabled: false }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%' }}
        >
          {children}
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

export default PinchViewerFrame;
